import { CartAddEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * Botones "+ Agregar" de las sugerencias del carrito (snippets/koya-cart-envio.liquid).
 * Suman la(s) variante(s) exacta(s) con /cart/add.js sin salir de la página.
 *
 * Para que se sienta rápido:
 * - Un solo pedido: /cart/add.js devuelve también la sección del carrito ya renderizada
 *   (sections) y la reemplazamos acá mismo, igual que hace el theme al cambiar cantidades
 *   (en el carrito lateral con el modo que lo deja abierto). Después avisamos con el
 *   evento de carrito para que se actualice el contador del ícono.
 * - Respuesta inmediata: toda sugerencia cierra el envío gratis, así que la barra pasa a
 *   "¡Tienes envío gratis!" apenas se toca el botón; si el pedido falla, vuelve atrás.
 *
 * data-koya-cart-add="<id de variante>:<cantidad>[,<id>:<cantidad>]"
 */
if (!window.koyaCartEnvioReady) {
  window.koyaCartEnvioReady = true;

  const FREE_COLOR = 'rgb(16 132 116)';

  /** Muestra el estado final en todas las barras de la página y devuelve cómo deshacerlo. */
  const showOptimisticFree = () => {
    const undo = [];
    document.querySelectorAll('[data-koya-envio]').forEach((wrapper) => {
      const html = wrapper.innerHTML;
      undo.push(() => (wrapper.innerHTML = html));

      const bar = wrapper.querySelector('.koya-envio__bar');
      const text = wrapper.querySelector('.koya-envio__text');
      const fill = wrapper.querySelector('.koya-envio__fill');
      if (bar instanceof HTMLElement && text && fill instanceof HTMLElement) {
        bar.classList.add('koya-envio__bar--free');
        bar.style.setProperty('--koya-envio-color', FREE_COLOR);
        text.innerHTML = '<strong>✓ ¡Tienes envío gratis!</strong>';
        fill.style.width = '100%';
      }
      wrapper.classList.add('koya-envio--adding');
      wrapper.querySelectorAll('[data-koya-cart-add]').forEach((button) => button.setAttribute('disabled', ''));
    });
    return () => undo.forEach((fn) => fn());
  };

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-koya-cart-add]') : null;
    if (!(target instanceof HTMLButtonElement) || target.disabled) return;

    const items = (target.dataset.koyaCartAdd || '')
      .split(',')
      .map((pair) => {
        const [id, quantity] = pair.split(':');
        return { id: Number(id), quantity: Number(quantity) || 1 };
      })
      .filter((item) => item.id);
    if (!items.length) return;

    // Carrito presente en la página (el de /cart o el carrito lateral)
    const cartComponents = [...document.querySelectorAll('cart-items-component[data-section-id]')].filter(
      (component) => component instanceof HTMLElement
    );
    const sectionIds = cartComponents.map((component) => /** @type {HTMLElement} */ (component).dataset.sectionId);
    const cartIcon = document.querySelector('cart-icon');
    const previousCount = Number(/** @type {any} */ (cartIcon)?.currentCartCount) || 0;
    const addedCount = items.reduce((sum, item) => sum + item.quantity, 0);

    // El aviso anticipado de envío gratis solo vale para las sugerencias de la barra; otros
    // botones del sitio (carrusel de la home) agregan un producto que puede no alcanzar.
    const undo = target.closest('[data-koya-envio]') ? showOptimisticFree() : () => {};

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: [...new Set(sectionIds)].join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || `cart/add ${response.status}`);

      const cartEvent = new CartAddEvent(data, 'koya-cart-envio', {
        source: 'koya-cart-envio',
        itemCount: previousCount + addedCount,
        variantId: String(items[0].id),
      });

      let morphed = false;
      for (const component of cartComponents) {
        const element = /** @type {HTMLElement} */ (component);
        const sectionId = element.dataset.sectionId;
        const html = sectionId ? data.sections?.[sectionId] : null;
        if (!sectionId || !html) continue;
        await morphSection(sectionId, html, { mode: element.hasAttribute('data-drawer') ? 'hydration' : 'full' });
        morphed = true;
      }

      // Si ya reemplazamos la sección, el evento sale desde el propio carrito para que no
      // la vuelva a pedir; si no, sale del documento y el theme la renderiza como siempre.
      const emitter = morphed && cartComponents[0]?.isConnected ? cartComponents[0] : document;
      emitter.dispatchEvent(cartEvent);

      // Botones de compra rápida fuera del carrito (home, tienda, artículos): se abre el carrito
      // lateral. Las sugerencias de la barra de envío ya están dentro del carrito.
      if (!target.closest('[data-koya-envio]')) {
        /** @type {any} */ (document.querySelector('cart-drawer-component'))?.open?.();
      }
    } catch (error) {
      console.error(error);
      undo();
    }
  });
}
